import { useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import { graphql } from '@/gql';
import { GetProjectStockSourcesQuery } from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';

const GetProjectStockSources = graphql(`
  query GetProjectStockSources($projectId: ID!, $pagination: PaginationInput!) {
    projectById(id: $projectId) {
      id
      materialsList {
        id
        publicId
        stockSources(
          input: { holderTypes: [STOCK_LOCATION] }
          pagination: $pagination
        ) {
          items {
            name
            materialsList {
              id
              publicId
            }
            materials {
              material {
                id
                name
                image {
                  id
                  thumbnailUrl
                }
              }
              outstandingQuantity
              onHandQuantity
              maxQuantity
            }
          }
        }
      }
    }
  }
`);

type ProjectStockSourcesField = NonNullable<
  GetProjectStockSourcesQuery['projectById']['materialsList']
>['stockSources'];

export type ProjectStockSource = ProjectStockSourcesField['items'][number];

export type ProjectStockSourceMaterial =
  ProjectStockSource['materials'][number];

/**
 * The Stock Locations that can supply the Project's still-needed materials,
 * driving the "move stock into the Project" path of the Create-Order wizard.
 * Returns an empty list when the caller lacks the stock feature flag or
 * permissions, so the source picker simply shows suppliers as it does today.
 */
const useGetProjectStockSources = (projectId?: string) => {
  const { data, ...rest } = useQuery({
    enabled: !!projectId,
    queryKey: projectKeys.stockSources(projectId!),
    queryFn: () =>
      gqlClient.request(GetProjectStockSources, {
        projectId: projectId!,
        // A large page so best-coverage-first ordering keeps every useful
        // source on the first (only) page.
        pagination: { ...PAGINATION_ARG_DEFAULTS, limit: 200 },
      }),
  });

  return {
    stockSources: data?.projectById.materialsList?.stockSources.items ?? [],
    ...rest,
  };
};

export default useGetProjectStockSources;
